import { Float32BufferAttribute, type BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { clothMembrane } from './shipSolids';
import { MAST_PAINTER_WIDTH_SCALE, MAST_RADIUS } from './rigParts';

export const PENNANT_LENGTH_PER_SHIP_LENGTH = 0.6;

/*
 * In the 1376 x 1000 painter frame (f = 2681 px) the harbour ships lie 2.74-2.96 km from the camera
 * at 43-48 degrees depression (docs/reference/ship-measure/summary.json). The ship's vertical
 * painter scale of 3.5 stretches the 1.2 m hoist to 4.2 m, 2.8 px across (4.2 m x cos 46 deg x
 * 2681 px / 2800 m). The painted streamers are 3.9 px at the hoist, tapering to a median of 1.2-1.9
 * px along (pennants.json thickness): this widens ours to 3.5-4.1 px. At 1.8 px, their round-2
 * width, the paint pass washed them into the water and the outline pass inked what was left, 61%
 * of them darker than L 25 and 1% red; the flying view keeps the full 1.2 m.
 */
export const PENNANT_PAINTER_HOIST_SCALE = 1.35;

/* The head sits just below the top of the staff; a whole number of half waves puts the tip on
   the wind line, and the wave grows from nothing at the staff to its full sway at the fly. */
const PENNANT = { hoist: 1.2, inset: 0.1, halfWaves: 5, sway: 0.04, cells: 20 } as const;
const UNIT_TOLERANCE = 1e-9;

/*
 * The painter's red streamers are red and white: pale bands cross them from the hoist to the tail
 * (R7, R8, R18, R26, R33, R34; scan 5720,3900-5840,3935 and 2440,4480-2520,4530 show 8 and 3-4 of
 * them), each about as long as the streamer is deep at the hoist. Of the pennant's 20 cells, six
 * spread evenly along it are pale (docs/plans/ships.md 13).
 */
export const PENNANT_BANDS = {
  cells: PENNANT.cells,
  at: [2, 5, 8, 11, 14, 17],
  proud: 0.015,
} as const;

/* A band lies over its cell on both faces, just off the cloth. At the painter's distance a band
   one cell long draws a pixel or two, and the paint pass washes the red out of the streamer
   around it, so the painter view shrinks each band to a point and shows the cloth red, as the
   painter's streamers read there (docs/plans/ships.md 13). */
function buildBand(
  builder: MeshBuilder,
  style: VertexStyle,
  columns: readonly (readonly [Vec3, Vec3])[],
  c: number
): VertexRange[] {
  const [left, right] = [columns[c]!, columns[c + 1]!];
  const [dx, dz] = [right[0][0] - left[0][0], right[0][2] - left[0][2]];
  const run = Math.hypot(dx, dz);
  const normal: Vec3 = [-dz / run, 0, dx / run];
  return [1, -1].map((side) => {
    const off = (p: Vec3): Vec3 => [
      p[0] + side * PENNANT_BANDS.proud * normal[0],
      p[1],
      p[2] + side * PENNANT_BANDS.proud * normal[2],
    ];
    const start = builder.vertexCount;
    clothMembrane(builder, style, [
      [off(left[0]), off(right[0])],
      [off(left[1]), off(right[1])],
    ]);
    return { start, end: builder.vertexCount };
  });
}

export interface PennantSpec {
  /** Top of the pennant's staff, on its mast's axis, in the ship's frame. */
  readonly staffTop: Vec3;
  readonly length: number;
  /** Horizontal unit vector along which the wind blows. */
  readonly downwind: Vec3;
}

/** A run of vertices [start, end) in the builder. */
export interface VertexRange {
  readonly start: number;
  readonly end: number;
}

/** The pennant's cloth [start, end) in the builder, the height of its centre line, and its pale
    bands, each a patch of its own. */
export interface PennantSpan extends VertexRange {
  readonly centreY: number;
  readonly bands: readonly VertexRange[];
}

function validate(spec: PennantSpec): void {
  if (!(spec.length > 0)) throw new Error(`Pennant needs a positive length, got ${spec.length}`);
  const [x, y, z] = spec.downwind;
  if (y !== 0 || Math.abs(Math.hypot(x, z) - 1) > UNIT_TOLERANCE) {
    throw new Error(`Pennant needs a horizontal unit downwind, got ${spec.downwind.join(', ')}`);
  }
}

/**
 * A pennant: a double-sided ribbon hung from its staff, 1.2 m at the hoist and tapering to a
 * point downwind, waving in plan, crossed by pale bands in `bands` where given. Returns its vertex
 * span for the painter hoist.
 */
export function buildPennant(
  builder: MeshBuilder,
  style: VertexStyle,
  spec: PennantSpec,
  bands?: VertexStyle
): PennantSpan {
  validate(spec);
  const [dx, , dz] = spec.downwind;
  const [hx, hy, hz] = spec.staffTop;
  const centreY = hy - PENNANT.inset - PENNANT.hoist / 2;
  const columns = Array.from({ length: PENNANT.cells + 1 }, (_, c): readonly [Vec3, Vec3] => {
    const t = c / PENNANT.cells;
    const reach = t * spec.length;
    const sway = PENNANT.sway * spec.length * t * Math.sin(Math.PI * PENNANT.halfWaves * t);
    const [x, z] = [hx + reach * dx - sway * dz, hz + reach * dz + sway * dx];
    const half = (PENNANT.hoist / 2) * (1 - t);
    return [
      [x, centreY + half, z],
      [x, centreY - half, z],
    ];
  });
  const start = builder.vertexCount;
  clothMembrane(builder, style, [columns.map(([top]) => top), columns.map(([, foot]) => foot)]);
  const end = builder.vertexCount;
  const bandSpans = bands
    ? PENNANT_BANDS.at.flatMap((c) => buildBand(builder, bands, columns, c))
    : [];
  return { start, end, centreY, bands: bandSpans };
}

/**
 * A spar or rope a pennant must end short of: its axis from `from` to `to`, and how far out from
 * the axis the painter view draws it.
 */
export interface PennantObstacle {
  readonly from: Vec3;
  readonly to: Vec3;
  readonly room: number;
}

/* A pennant streaming along the centre line would pass through a mast behind it that rises to its
   height, with the wind within a few degrees of the ship's axis: it ends this far short of the
   first spar or rope at its height that stands within the pennant's sway of its line. */
const MAST_CLEARANCE = 0.2;
const SAMPLE_STEP = 0.2;

/** Every mast as its staff shows it, from far below up to the top of the staff. */
export function staffObstacles(tops: readonly Vec3[]): readonly PennantObstacle[] {
  const room = MAST_RADIUS * MAST_PAINTER_WIDTH_SCALE;
  return tops.map((top) => ({ from: [top[0], -1000, top[2]], to: top, room }));
}

/* The stretch of an obstacle between the heights a pennant flies at, as parameters along it. */
function withinHoist(own: Vec3, obstacle: PennantObstacle): readonly [number, number] | null {
  const [low, high] = [own[1] - PENNANT.inset - PENNANT.hoist, own[1] - PENNANT.inset];
  const [y0, dy] = [obstacle.from[1], obstacle.to[1] - obstacle.from[1]];
  if (Math.abs(dy) < UNIT_TOLERANCE) return y0 >= low && y0 <= high ? [0, 1] : null;
  const [a, b] = [(low - y0) / dy, (high - y0) / dy];
  const [t0, t1] = [Math.max(0, Math.min(a, b)), Math.min(1, Math.max(a, b))];
  return t0 <= t1 ? [t0, t1] : null;
}

/* How far downwind the pennant may fly before `obstacle`, or null if it never meets it. */
function inTheWay(own: Vec3, obstacle: PennantObstacle, downwind: Vec3): number | null {
  const stretch = withinHoist(own, obstacle);
  if (!stretch) return null;
  const { from, to } = obstacle;
  const span =
    Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]) * (stretch[1] - stretch[0]);
  const steps = Math.max(1, Math.ceil(span / SAMPLE_STEP));
  const room = obstacle.room + MAST_CLEARANCE;
  let reach: number | null = null;
  for (let k = 0; k <= steps; k++) {
    const t = stretch[0] + ((stretch[1] - stretch[0]) * k) / steps;
    const [ax, az] = [
      from[0] + (to[0] - from[0]) * t - own[0],
      from[2] + (to[2] - from[2]) * t - own[2],
    ];
    const along = ax * downwind[0] + az * downwind[2];
    const across = Math.abs(ax * downwind[2] - az * downwind[0]);
    if (along > 0 && across < room + PENNANT.sway * along)
      reach = Math.min(reach ?? Infinity, along - room);
  }
  return reach;
}

/**
 * How far each mast's pennant flies: `full`, or short of the first spar or rope standing in its
 * wind line at its height; by default the other masts, as their staffs show them.
 */
export function pennantLengths(
  tops: readonly Vec3[],
  downwind: Vec3,
  full: number,
  obstacles: readonly PennantObstacle[] = staffObstacles(tops)
): readonly number[] {
  return tops.map((own) =>
    obstacles.reduce(
      (length, obstacle) => Math.min(length, inTheWay(own, obstacle, downwind) ?? length),
      full
    )
  );
}

function shrinkToCentre(
  position: BufferGeometry['attributes'][string],
  offsets: Float32Array,
  { start, end }: VertexRange
): void {
  const centre = [0, 1, 2].map((k) => {
    let sum = 0;
    for (let i = start; i < end; i++) sum += position.getComponent(i, k);
    return sum / (end - start);
  });
  for (let i = start; i < end; i++) {
    for (let k = 0; k < 3; k++) offsets[i * 3 + k] = centre[k]! - position.getComponent(i, k);
  }
}

/**
 * Widens every pennant's hoist about its centre line in the painter view, and shrinks its pale
 * bands to points there. The offset differs per vertex while the builder styles whole faces, so it
 * is written once the geometry is built.
 */
export function withPainterHoists(
  geometry: BufferGeometry,
  spans: readonly PennantSpan[]
): BufferGeometry {
  const position = geometry.getAttribute('position');
  const current = geometry.getAttribute('aAxisOffset');
  const offsets = current ? Float32Array.from(current.array) : new Float32Array(position.count * 3);
  for (const span of spans) {
    for (let i = span.start; i < span.end; i++) {
      offsets[i * 3 + 1] = (PENNANT_PAINTER_HOIST_SCALE - 1) * (position.getY(i) - span.centreY);
    }
    span.bands.forEach((band) => {
      shrinkToCentre(position, offsets, band);
    });
  }
  geometry.setAttribute('aAxisOffset', new Float32BufferAttribute(offsets, 3));
  return geometry;
}
